import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Crown, Building2, Users, ShoppingCart, Zap, Calendar, ArrowUpDown, Search, 
  Trash2, AlertTriangle, CheckSquare, Square 
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

export default function SuperAdminPage() {
  const { api } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [updatingPlan, setUpdatingPlan] = useState(null);
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => { fetchRestaurants(); }, []);

  const fetchRestaurants = async () => {
    try {
      const res = await api.get('/super-admin/restaurants');
      setRestaurants(res.data);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePlan = async (restaurant) => {
    const newPlan = restaurant.plan === 'pro' ? 'free' : 'pro';
    setUpdatingPlan(restaurant.id);
    try {
      await api.put(`/super-admin/restaurants/${restaurant.id}/plan`, { plan: newPlan });
      setRestaurants(prev => prev.map(r => r.id === restaurant.id ? { ...r, plan: newPlan } : r));
      toast.success(`${restaurant.name} → plan ${newPlan.toUpperCase()}`);
    } catch (e) {
      toast.error('Erreur lors du changement de plan');
    } finally {
      setUpdatingPlan(null);
    }
  };

  const toggleSelectRestaurant = (restaurantId) => {
    setSelectedRestaurants(prev => 
      prev.includes(restaurantId) 
        ? prev.filter(id => id !== restaurantId)
        : [...prev, restaurantId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedRestaurants.length === filtered.length) {
      setSelectedRestaurants([]);
    } else {
      setSelectedRestaurants(filtered.map(r => r.id));
    }
  };

  const handleDeleteSelected = async () => {
    setIsDeleting(true);
    try {
      await api.post('/super-admin/restaurants/delete-multiple', {
        restaurant_ids: selectedRestaurants
      });
      
      toast.success(`${selectedRestaurants.length} restaurant(s) supprimé(s) avec succès`);
      setSelectedRestaurants([]);
      setDeleteDialogOpen(false);
      fetchRestaurants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const filtered = restaurants.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.owner_email?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPro = restaurants.filter(r => r.plan === 'pro').length;
  const totalFree = restaurants.filter(r => r.plan === 'free').length;
  const totalOrders = restaurants.reduce((s, r) => s + (r.orders_count || 0), 0);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]" />
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Crown className="w-6 h-6 text-amber-500" /> Super Admin
        </h1>
        <p className="text-gray-500 text-sm mt-1">Gestion de tous les restaurants OrderLab</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Building2} label="Total restaurants" value={restaurants.length} color="blue" />
        <StatCard icon={Crown} label="Plan Pro" value={totalPro} color="green" />
        <StatCard icon={Zap} label="Plan Gratuit" value={totalFree} color="amber" />
        <StatCard icon={ShoppingCart} label="Total commandes" value={totalOrders} color="purple" />
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un restaurant ou email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              data-testid="super-admin-search"
            />
          </div>
          
          {/* Delete button - Only show if restaurants are selected */}
          {selectedRestaurants.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(true)}
              className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
              data-testid="delete-selected-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer ({selectedRestaurants.length})
            </Button>
          )}
        </div>

        {/* Select all checkbox */}
        {filtered.length > 0 && (
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="select-all-btn"
            >
              {selectedRestaurants.length === filtered.length ? (
                <CheckSquare className="w-5 h-5 text-[#1B4332]" />
              ) : (
                <Square className="w-5 h-5" />
              )}
              <span className="font-medium">
                {selectedRestaurants.length === filtered.length 
                  ? 'Tout désélectionner' 
                  : 'Tout sélectionner'}
              </span>
            </button>
            {selectedRestaurants.length > 0 && (
              <span className="text-sm text-gray-500">
                {selectedRestaurants.length} restaurant(s) sélectionné(s)
              </span>
            )}
          </div>
        )}

        <div className="space-y-3">
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Building2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>Aucun restaurant trouvé</p>
            </div>
          )}
          {filtered.map(r => (
            <div key={r.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors" data-testid={`restaurant-row-${r.id}`}>
              {/* Checkbox */}
              <button
                onClick={() => toggleSelectRestaurant(r.id)}
                className="shrink-0"
                data-testid={`select-restaurant-${r.id}`}
              >
                {selectedRestaurants.includes(r.id) ? (
                  <CheckSquare className="w-5 h-5 text-[#1B4332]" />
                ) : (
                  <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
              
              <div className="flex items-center justify-between flex-1">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#1B4332] rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {r.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{r.name}</p>
                      <Badge className={r.plan === 'pro' ? 'bg-[#1B4332] text-white text-xs' : 'bg-amber-100 text-amber-700 text-xs'}>
                        {r.plan === 'pro' ? <><Crown className="w-3 h-3 mr-1" />Pro</> : 'Free'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500">{r.owner_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="hidden md:flex items-center gap-6 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><Users className="w-4 h-4" />{r.users_count}</span>
                    <span className="flex items-center gap-1"><Building2 className="w-4 h-4" />{r.suppliers_count} fournisseurs</span>
                    <span className="flex items-center gap-1"><ShoppingCart className="w-4 h-4" />{r.orders_count} cmd</span>
                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />
                      {r.created_at ? (() => { try { return format(new Date(r.created_at), 'dd/MM/yyyy', { locale: fr }); } catch { return r.created_at?.slice(0,10); } })() : '-'}
                    </span>
                  </div>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleTogglePlan(r)}
                    disabled={updatingPlan === r.id}
                    className={`text-xs ${r.plan === 'pro' ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-700 hover:bg-green-50'}`}
                    data-testid={`toggle-plan-${r.id}`}
                  >
                    {updatingPlan === r.id ? '...' : r.plan === 'pro' ? '→ Passer Free' : '→ Passer Pro'}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Supprimer {selectedRestaurants.length} restaurant(s) ?
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p className="text-gray-700 font-medium">
                ⚠️ Cette action est <span className="text-red-600 font-bold">IRRÉVERSIBLE</span>
              </p>
              <p className="text-gray-600">
                Toutes les données associées seront <span className="font-semibold">définitivement supprimées</span> :
              </p>
              <ul className="text-sm text-gray-600 space-y-1 pl-4">
                <li>• Tous les utilisateurs du/des restaurant(s)</li>
                <li>• Tous les fournisseurs</li>
                <li>• Tous les produits</li>
                <li>• Toutes les commandes</li>
                <li>• Toutes les livraisons</li>
                <li>• Tous les modèles de commande</li>
              </ul>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
                <p className="text-sm text-red-800 font-medium">
                  Restaurants sélectionnés :
                </p>
                <ul className="text-sm text-red-700 mt-2 space-y-1">
                  {selectedRestaurants.map(id => {
                    const restaurant = restaurants.find(r => r.id === id);
                    return restaurant ? (
                      <li key={id}>• {restaurant.name} ({restaurant.owner_email})</li>
                    ) : null;
                  })}
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Annuler
            </Button>
            <Button
              onClick={handleDeleteSelected}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Oui, supprimer définitivement
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className={`inline-flex p-2 rounded-lg ${colors[color]} mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}
